import { createSelector } from '@ngrx/store';
import { BookingAmanoParkingReservation, BookingTariff } from '@products/models/booking.model';
import { getAllBookedTariffs, getBookedTariff } from '@store/products/booking/product/product-booking.selectors';

/**
 * Get all booked Amano parking tariffs
 * @description Get all booked Amano parking tariffs with Amano parking reservation
 * @return Booked tariff or empty array
 */
export const getAllBookedAmanoParkingTariffs = createSelector(getAllBookedTariffs, (bookedTariffs: BookingTariff[]) => {
  const AmanoParkingBookedTariffs: BookingTariff[] = [];

  bookedTariffs.forEach((bookedTariff) => {
    if (bookedTariff.amanoParkingReservations.length) {
      AmanoParkingBookedTariffs.push(bookedTariff);
    }
  });

  return AmanoParkingBookedTariffs;
});

/**
 * Get all booked Amano parking reservations
 * @description Get all booked Amano parking reservations by ticketIndex returned from API response
 * @return Amano parking reservation or empty array
 */
export const getAllBookedAmanoParkingReservations = createSelector(
  getAllBookedAmanoParkingTariffs,
  (bookedTariffs: BookingTariff[]) => {
    const bookedAmanoParkingReservations: BookingAmanoParkingReservation[] = [];

    bookedTariffs.forEach((bookedTariff) =>
      bookedTariff.amanoParkingReservations.forEach((bookedAmanoParkingReservation) =>
        bookedAmanoParkingReservations.push(bookedAmanoParkingReservation)
      )
    );

    return bookedAmanoParkingReservations;
  }
);

/**
 * Are all booked Amano parking reservations valid
 * @description Check if all booked Amano parking reservations are valid
 * @returns Boolean
 */
export const areBookedAmanoParkingReservationsValid = createSelector(
  getAllBookedAmanoParkingReservations,
  (bookedAmanoParkingReservations: BookingAmanoParkingReservation[]) =>
    bookedAmanoParkingReservations.every((bookingAmanoParkingReservation) => bookingAmanoParkingReservation.isValid)
);

/**
 * Get booked tariff Amano parking reservations
 * @description Get booked tariff Amano parking reservations array
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Amano parking reservation or empty array
 */
export const getBookedTariffAmanoParkingReservations = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getBookedTariff(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    (bookedTariff: BookingTariff): BookingAmanoParkingReservation[] =>
      bookedTariff ? bookedTariff.amanoParkingReservations : []
  );

/**
 * Are booked tariff Amano parking reservations valid
 * @description Check if booked tariff Amano parking reservations are valid
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Boolean
 */
export const areBookedTariffAmanoParkingReservationsValid = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getBookedTariffAmanoParkingReservations(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    (bookedAmanoParkingReservations: BookingAmanoParkingReservation[]) =>
      bookedAmanoParkingReservations.every((bookedAmanoParkingReservation) => bookedAmanoParkingReservation.isValid)
  );

/**
 * Get booked tariff Amano parking reservations with selected date
 * @description Get booked tariff Amano parking reservations array with selected date
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Amano parking reservation or empty array
 */
export const getBookedTariffAmanoParkingReservationsWithSelectedDate = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getBookedTariffAmanoParkingReservations(ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex),
    (bookedAmanoParkingReservations: BookingAmanoParkingReservation[]): BookingAmanoParkingReservation[] =>
      bookedAmanoParkingReservations.filter((bookedAmanoParkingReservation) => bookedAmanoParkingReservation.since)
  );

/**
 * Get booked tariff Amano parking reservation total price
 * @description Get booked tariff Amano parking reservation total price
 * @param ticketTypeId
 * @param ticketPersonId
 * @param voucherCode
 * @param packageNumber
 * @param packageIndex
 * @returns Number
 */
export const getBookedTariffAmanoParkingReservationTotalPrice = (
  ticketTypeId: number,
  ticketPersonId: number,
  voucherCode?: string,
  packageNumber?: number,
  packageIndex?: number
) =>
  createSelector(
    getBookedTariffAmanoParkingReservationsWithSelectedDate(
      ticketTypeId,
      ticketPersonId,
      voucherCode,
      packageNumber,
      packageIndex
    ),
    (bookedAmanoParkingReservations: BookingAmanoParkingReservation[]): number =>
      bookedAmanoParkingReservations.reduce(
        (bookedAmanoParkingReservationTotalPrice, bookedAmanoParkingReservation) =>
          bookedAmanoParkingReservationTotalPrice + bookedAmanoParkingReservation.price,
        0
      )
  );

/**
 * Get number of booked amano parking tariffs count
 * @descriptionGet Number of booked amano parking tariffs count
 * @returns Number
 */
export const getAllBookedAmanoParkingTariffsCount = createSelector(
  getAllBookedTariffs,
  (allProductsTotalPrice: BookingTariff[]): BookingTariff[] =>
    allProductsTotalPrice.filter((bookedTariff) => bookedTariff.amanoParkingReservations.length > 0)
);
